'use client';

import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Circle } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/slw/shared/lib/utils';

const radioVariants = cva(
  `aspect-square rounded-full border border-primary text-primary ring-offset-background focus:outline-none 
        focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed 
        disabled:opacity-50 data-[state=unchecked]:transition-border data-[state=unchecked]:duration-200
        hover:border-default`,
  {
    variants: {
      color: {
        grey: [
          'border-gray-300',
          'bg-gray-300',
          'data-[state=checked]:border-default',
          'data-[state=checked]:bg-primary',
          'data-[state=checked]:text-gray-300',
        ],
        dark: [
          'border-dark-grey-input',
          'bg-dark-grey-input',
          'hover:border-primary',
          'data-[state=checked]:border-default',
          'data-[state=checked]:bg-primary',
          'data-[state=checked]:text-dark-grey-input',
        ],
        white: [
          'border-white',
          'bg-white',
          'data-[state=checked]:border-default',
          'data-[state=checked]:bg-primary',
          'data-[state=checked]:text-secondary',
        ],
      },
      size: {
        sm: 'size-4 [&_svg]:size-4 data-[state=checked]:size-6',
        md: 'size-5 [&_svg]:size-5 data-[state=checked]:size-7',
        lg: 'size-7 [&_svg]:size-7 data-[state=checked]:size-9',
      },
    },
    defaultVariants: {
      color: 'white',
      size: 'md',
    },
  },
);

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return <RadioGroupPrimitive.Root className={cn('grid gap-2', className)} {...props} ref={ref} />;
});

RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & {
    color?: VariantProps<typeof radioVariants>['color'];
    size?: VariantProps<typeof radioVariants>['size'];
  }
>(({ className, color = 'white', size = 'md', ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item ref={ref} className={cn(radioVariants({ color, size }), className)} {...props}>
      <RadioGroupPrimitive.Indicator className='flex items-center justify-center'>
        <Circle className='fill-current text-current' />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});

RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
